// src/Agences.js - Page de gestion des agences Godot & Fils
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './styles/ManagementPages.css';

// Formater une date en français
const formatDateFr = (dateString) => {
  if (!dateString) return '';
  // Ignorer les dates invalides (1899, 0000, etc.)
  if (dateString.includes('1899') || dateString.includes('0000')) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch {
    return dateString;
  }
};

const Agences = () => {
  const [agences, setAgences] = useState([]);
  const [villes, setVilles] = useState([]);
  const [stats, setStats] = useState({ total: 0, ouvertes: 0 });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVille, setSelectedVille] = useState('');
  const [franchiseFilter, setFranchiseFilter] = useState('all'); // all, true, false
  const [onlyOpen, setOnlyOpen] = useState(false);
  
  // Modal détail
  const [selectedAgence, setSelectedAgence] = useState(null);

  const fetchAgences = useCallback(async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      
      const params = {};
      if (selectedVille) params.ville = selectedVille;
      if (franchiseFilter !== 'all') params.franchise = franchiseFilter === 'true';
      if (onlyOpen) params.onlyOpen = true;
      
      const response = await axios.get('/api/agences', {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      let data = response.data.data;
      
      // Filtre local par recherche
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        data = data.filter(a => 
          a.nom?.toLowerCase().includes(term) ||
          a.ville?.toLowerCase().includes(term) ||
          a.adresse?.toLowerCase().includes(term) ||
          a.cp?.includes(term)
        );
      }
      
      setAgences(data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Erreur lors de la récupération des agences');
    } finally {
      setIsLoading(false);
    }
  }, [selectedVille, franchiseFilter, onlyOpen, searchTerm]);

  const fetchVilles = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/agences/villes', {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setVilles(response.data.data);
    } catch (err) {
      console.error('Erreur villes:', err);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const [totalRes, ouvertesRes] = await Promise.all([
        axios.get('/api/agences/count', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }),
        axios.get('/api/agences/count?onlyOpen=true', {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        })
      ]);
      setStats({
        total: totalRes.data.count,
        ouvertes: ouvertesRes.data.count
      });
    } catch (err) {
      console.error('Erreur stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchVilles();
    fetchStats();
  }, [fetchVilles, fetchStats]);

  useEffect(() => {
    fetchAgences();
  }, [fetchAgences]);

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedVille('');
    setFranchiseFilter('all');
    setOnlyOpen(false);
  };

  const hasActiveFilters = 
    searchTerm.trim() !== '' || 
    selectedVille !== '' ||
    franchiseFilter !== 'all' ||
    onlyOpen;

  const gmbUrl = (agence) => {
    if (agence.google_my_business) return agence.google_my_business;
    const query = ['Godot et Fils', agence.adresse, `${agence.cp || ''} ${agence.ville || ''}`]
      .filter(Boolean)
      .join(', ')
      .replace(/\s+/g, ' ')
      .trim();
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  };

  const openGoogleMyBusiness = (agence) => {
    window.open(gmbUrl(agence), '_blank', 'noopener,noreferrer');
  };

  const gmbConfirmedCount = agences.filter((a) => a.google_my_business_confirmed).length;

  return (
    <div className="management-page">
      <div className="management-page__inner">
        <header className="management-header glass-panel">
          <div className="management-header__content">
            <h1 className="management-title">
              <span className="management-title__icon">🏢</span>
              Agences Godot & Fils
            </h1>
            <p className="management-subtitle">
              Consultez l'ensemble des agences du réseau Godot & Fils. Filtrez par ville, 
              type (franchise ou succursale) et accédez aux informations détaillées de chaque agence.
            </p>
            <div className="management-meta">
              <span className="management-badge">
                {stats.total} agences au total
              </span>
              <span className="management-badge">
                {stats.ouvertes} agences ouvertes
              </span>
            </div>
          </div>
          <div className="management-header__actions">
            <button 
              type="button" 
              className="mgmt-btn mgmt-btn--outline" 
              onClick={() => {
                fetchAgences();
                fetchStats();
              }}
            >
              🔄 Actualiser
            </button>
          </div>
        </header>

        <section className="management-stats">
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Total agences</span>
            <span className="management-stat-value">{stats.total}</span>
            <span className="management-stat-meta">Réseau complet</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Agences ouvertes</span>
            <span className="management-stat-value">{stats.ouvertes}</span>
            <span className="management-stat-meta">En activité</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Villes</span>
            <span className="management-stat-value">{villes.length}</span>
            <span className="management-stat-meta">Couverture France</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Affichées</span>
            <span className="management-stat-value">{agences.length}</span>
            <span className="management-stat-meta">Selon filtres</span>
          </div>
          <div className="management-stat-card glass-panel">
            <span className="management-stat-title">Liens Google</span>
            <span className="management-stat-value">{agences.length}</span>
            <span className="management-stat-meta">{gmbConfirmedCount} fiches GMB · {Math.max(0, agences.length - gmbConfirmedCount)} Google Maps</span>
          </div>
        </section>

        {error && (
          <div className="management-alert error">
            <span>⚠️</span>
            {error}
          </div>
        )}

        <section className="management-section glass-panel">
          <div className="management-section__head">
            <h2 className="management-section__title">Filtrer les agences</h2>
            <p className="management-section__subtitle">
              Recherchez par nom, ville, code postal ou filtrez par type d'agence.
            </p>
          </div>
          
          <div className="management-grid management-grid--two">
            <div className="management-form__field">
              <label htmlFor="agence-search" className="management-form__label">
                🔍 Recherche
              </label>
              <input
                id="agence-search"
                className="mgmt-input"
                type="text"
                placeholder="Nom, ville, adresse, code postal..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="management-form__field">
              <label htmlFor="ville-filter" className="management-form__label">
                📍 Ville
              </label>
              <select
                id="ville-filter"
                className="mgmt-input"
                value={selectedVille}
                onChange={(e) => setSelectedVille(e.target.value)}
              >
                <option value="">Toutes les villes</option>
                {villes.map((v) => (
                  <option key={v.ville} value={v.ville}>
                    {v.ville} ({v.count})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="management-grid management-grid--two" style={{ marginTop: '20px' }}>
            <div className="management-form__field">
              <label htmlFor="franchise-filter" className="management-form__label">
                🏷️ Type d'agence
              </label>
              <select
                id="franchise-filter"
                className="mgmt-input"
                value={franchiseFilter}
                onChange={(e) => setFranchiseFilter(e.target.value)}
              >
                <option value="all">Toutes (franchises + succursales)</option>
                <option value="true">Franchises uniquement</option>
                <option value="false">Succursales uniquement</option>
              </select>
            </div>
            <div className="management-form__field">
              <label className="management-form__label">
                ⚙️ Options
              </label>
              <label className="management-checkbox" style={{ maxWidth: '300px' }}>
                <input
                  type="checkbox"
                  checked={onlyOpen}
                  onChange={(e) => setOnlyOpen(e.target.checked)}
                />
                Afficher uniquement les agences ouvertes
              </label>
            </div>
          </div>

          <div className="management-actions">
            <button
              type="button"
              className="mgmt-btn mgmt-btn--ghost"
              onClick={resetFilters}
            >
              🔄 Réinitialiser les filtres
            </button>
            {hasActiveFilters && (
              <span style={{ color: '#667eea', fontWeight: '600' }}>
                ✓ Filtres actifs
              </span>
            )}
          </div>
        </section>

        <section className="management-section glass-panel">
          <div className="management-section__head">
            <h2 className="management-section__title">Liste des agences</h2>
            <p className="management-section__subtitle">
              {agences.length} agence(s) trouvée(s)
            </p>
          </div>

          {isLoading ? (
            <div className="management-empty-state">
              <span className="management-empty-state__icon">⏳</span>
              <span className="management-empty-state__title">Chargement des agences...</span>
            </div>
          ) : agences.length === 0 ? (
            <div className="management-empty-state">
              <span className="management-empty-state__icon">🏢</span>
              <span className="management-empty-state__title">Aucune agence trouvée</span>
              <span className="management-empty-state__subtitle">
                Ajustez vos filtres ou réinitialisez la recherche.
              </span>
            </div>
          ) : (
            <div className="management-table-scroll">
              <table className="management-table">
                <thead>
                  <tr>
                    <th>Agence</th>
                    <th>Ville</th>
                    <th>Adresse</th>
                    <th>Téléphone</th>
                    <th>Type</th>
                    <th>Google My Business</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {agences.map((agence) => {
                const isFranchise = agence.franchise === 1;
                const isFerme = agence.fermeture === 1 || agence.status === 0;
                
                return (
                  <tr
                    key={agence.id}
                    style={{ cursor: 'pointer', opacity: isFerme ? 0.65 : 1 }}
                    onClick={() => setSelectedAgence(agence)}
                  >
                    <td>
                      <strong>{agence.nom}</strong>
                    </td>
                    <td>
                      {agence.ville}
                      {agence.cp ? ` (${agence.cp})` : ''}
                    </td>
                    <td>{agence.adresse || '—'}</td>
                    <td>
                      {agence.tel ? (
                        <a href={`tel:${agence.tel}`} onClick={(e) => e.stopPropagation()}>
                          {agence.tel}
                        </a>
                      ) : '—'}
                    </td>
                    <td>
                      <span className={`management-tag ${isFranchise ? 'management-tag--warning' : 'management-tag--success'}`}>
                        {isFranchise ? 'Franchise' : 'Succursale'}
                      </span>
                      {isFerme && (
                        <span className="management-tag" style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#dc2626', marginLeft: 6 }}>
                          Fermée
                        </span>
                      )}
                    </td>
                    <td>
                      <a
                        href={gmbUrl(agence)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mgmt-btn mgmt-btn--outline"
                        style={{ padding: '6px 10px', fontSize: '0.85rem', display: 'inline-flex' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {agence.google_my_business_confirmed ? '📍 Fiche Google' : '📍 Google Maps'}
                      </a>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="mgmt-btn mgmt-btn--ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAgence(agence);
                        }}
                      >
                        Détail
                      </button>
                    </td>
                  </tr>
                );
              })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* Modal détail agence */}
      {selectedAgence && (
        <div 
          className="agence-modal-overlay"
          onClick={() => setSelectedAgence(null)}
        >
          <div 
            className="agence-modal glass-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="agence-modal__close"
              onClick={() => setSelectedAgence(null)}
            >
              ✕
            </button>
            
            <div className="agence-modal__header">
              <h2>{selectedAgence.titre || selectedAgence.nom}</h2>
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <span className={`management-tag ${selectedAgence.franchise === 1 ? 'management-tag--warning' : 'management-tag--success'}`}>
                  {selectedAgence.franchise === 1 ? '🏷️ Franchise' : '🏛️ Succursale'}
                </span>
                {selectedAgence.fermeture === 1 && (
                  <span className="management-tag" style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#dc2626' }}>
                    🚫 Fermée temporairement
                  </span>
                )}
              </div>
            </div>

            <div className="agence-modal__content">
              {/* Images carousel */}
              {selectedAgence.images && selectedAgence.images.length > 0 && (
                <div className="agence-modal__images">
                  {selectedAgence.images.slice(0, 4).map((img, idx) => (
                    <img 
                      key={img.id} 
                      src={img.url} 
                      alt={`${selectedAgence.nom} - ${idx + 1}`}
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ))}
                </div>
              )}

              <div className="agence-modal__info">
                <div className="agence-modal__section">
                  <h3>📍 Coordonnées</h3>
                  <p><strong>Adresse:</strong> {selectedAgence.adresse}</p>
                  <p><strong>Code postal:</strong> {selectedAgence.cp}</p>
                  <p><strong>Ville:</strong> {selectedAgence.ville}</p>
                  {selectedAgence.tel && <p><strong>Téléphone:</strong> <a href={`tel:${selectedAgence.tel}`}>{selectedAgence.tel}</a></p>}
                  {selectedAgence.fax && <p><strong>Fax:</strong> {selectedAgence.fax}</p>}
                  {selectedAgence.email && <p><strong>Email:</strong> <a href={`mailto:${selectedAgence.email}`}>{selectedAgence.email}</a></p>}
                  {selectedAgence.manager && <p><strong>Manager:</strong> {selectedAgence.manager}</p>}
                  <p>
                    <strong>Google My Business:</strong>{' '}
                    <a href={gmbUrl(selectedAgence)} target="_blank" rel="noopener noreferrer">
                      {selectedAgence.google_my_business_confirmed ? 'Ouvrir la fiche' : 'Ouvrir Google Maps'}
                    </a>
                  </p>
                </div>

                {selectedAgence.ouverture && (
                  <div className="agence-modal__section">
                    <h3>🕐 Horaires</h3>
                    <p>{selectedAgence.ouverture}</p>
                  </div>
                )}

                {selectedAgence.description_fr && (
                  <div className="agence-modal__section">
                    <h3>📝 Description</h3>
                    <div 
                      className="agence-description-html"
                      dangerouslySetInnerHTML={{ __html: selectedAgence.description_fr }}
                    />
                  </div>
                )}

                {/* Afficher fermeture seulement si dates valides (pas 1899) et texte présent */}
                {(selectedAgence.fermeture === 1 || (selectedAgence.fermeture_texte && selectedAgence.fermeture_texte.trim())) && (
                  <div className="agence-modal__section" style={{ background: 'rgba(239, 68, 68, 0.08)', padding: '12px', borderRadius: '8px' }}>
                    <h3 style={{ color: '#dc2626' }}>⚠️ Fermeture</h3>
                    {selectedAgence.fermeture_texte && <p>{selectedAgence.fermeture_texte}</p>}
                    {selectedAgence.fermeture_from && selectedAgence.fermeture_to && 
                     !selectedAgence.fermeture_from.includes('1899') && 
                     !selectedAgence.fermeture_to.includes('1899') && (
                      <p>Du {formatDateFr(selectedAgence.fermeture_from)} au {formatDateFr(selectedAgence.fermeture_to)}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="agence-modal__actions">
                <button
                  type="button"
                  className="mgmt-btn mgmt-btn--primary"
                  onClick={() => openGoogleMyBusiness(selectedAgence)}
                >
                  {selectedAgence.google_my_business_confirmed ? '📍 Fiche Google' : '📍 Google Maps'}
                </button>
                {selectedAgence.link_website && (
                  <a
                    href={selectedAgence.link_website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mgmt-btn mgmt-btn--outline"
                  >
                    🌐 Site web
                  </a>
                )}
                <button
                  type="button"
                  className="mgmt-btn mgmt-btn--ghost"
                  onClick={() => setSelectedAgence(null)}
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .agence-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }
        
        .agence-modal {
          max-width: 700px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          padding: 24px;
          position: relative;
        }
        
        .agence-modal__close {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 32px;
          height: 32px;
          border: none;
          background: rgba(0, 0, 0, 0.1);
          border-radius: 50%;
          cursor: pointer;
          font-size: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .agence-modal__close:hover {
          background: rgba(0, 0, 0, 0.2);
        }
        
        .agence-modal__header {
          margin-bottom: 20px;
        }
        
        .agence-modal__header h2 {
          margin: 0;
          font-size: 24px;
          color: var(--color-text-primary);
        }
        
        .agence-modal__images {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 10px;
          margin-bottom: 20px;
        }
        
        .agence-modal__images img {
          width: 100%;
          height: 120px;
          object-fit: cover;
          border-radius: 8px;
          cursor: pointer;
          transition: transform 0.2s;
        }
        
        .agence-modal__images img:hover {
          transform: scale(1.05);
        }
        
        .agence-modal__info {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .agence-modal__section h3 {
          margin: 0 0 8px 0;
          font-size: 16px;
          color: var(--color-text-primary);
        }
        
        .agence-modal__section p {
          margin: 4px 0;
          font-size: 14px;
          color: var(--color-text-secondary);
        }
        
        .agence-modal__section a {
          color: #667eea;
          text-decoration: none;
        }
        
        .agence-modal__section a:hover {
          text-decoration: underline;
        }
        
        .agence-modal__actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
          flex-wrap: wrap;
        }
        
        /* Style pour le HTML de la description */
        .agence-description-html {
          font-size: 14px;
          line-height: 1.7;
          color: var(--color-text-secondary);
          max-height: 300px;
          overflow-y: auto;
          padding-right: 8px;
        }
        
        .agence-description-html p {
          margin: 0 0 12px 0;
        }
        
        .agence-description-html h2 {
          font-size: 16px;
          font-weight: 600;
          color: var(--color-text-primary);
          margin: 16px 0 8px 0;
        }
        
        .agence-description-html a {
          color: #667eea;
          text-decoration: none;
        }
        
        .agence-description-html a:hover {
          text-decoration: underline;
        }
        
        .agence-description-html strong {
          color: var(--color-text-primary);
        }
      `}</style>
    </div>
  );
};

export default Agences;
